// El guion de la demo del Asistente en Crear (ADR-2026-10-09-asistente-en-crear).
//
// Una conversación de mentira que corre UNA vez y queda terminada, sin volver a arrancar:
// Cumbre saluda y pregunta, el usuario escribe un pedido con la foto de su producto, Cumbre
// piensa, contesta y arma un carrusel de cuatro placas. Todo lo que se dice SE ESCRIBE letra
// por letra, de los dos lados (ADR-2026-10-09-asistente-en-crear-2). Puro: dado cuánto va
// desde que empezó, dice qué se ve. El componente sólo cuenta el tiempo y dibuja
// (`DemoDelAsistente`).

/** Lo que dice Cumbre al saludar, en tramos (el último va en azul). */
// "Asistente" y no "Cumbre": es el nombre de la pestaña (ADR-2026-10-13-el-asistente-se-presenta-como-asistente).
export const SALUDO_DE_LA_DEMO = ['¡Hola! Soy tu Asistente. ', '¿Qué te gustaría crear hoy?'] as const;
/** Lo que "escribe" el usuario en la demo. */
export const PEDIDO_DE_LA_DEMO = 'Armame un carrusel de 4 imágenes para Instagram con esta foto';
/** Lo que contesta Cumbre antes de armar. */
export const RESPUESTA_DE_LA_DEMO = '¡Me encanta! Ya armo las 4 imágenes con tu producto.';
/** El cierre, cuando el carrusel está listo. */
export const LISTO_DE_LA_DEMO = 'Carrusel listo · 4 imágenes para Instagram';

/** Un tramo de escritura: desde cuándo hasta cuándo se escribe un texto (ms). */
const TIPEO = {
  saludo: [500, 1_900],
  pedido: [2_500, 4_500],
  respuesta: [6_300, 7_500],
  listo: [9_900, 10_700],
} as const;

const SALUDO_MS = 400;
const PEDIDO_MS = 2_200;
const PIENSA_DESDE_MS = 4_900;
const RESPUESTA_MS = 6_200;
const ARMADO_HASTA_MS = 9_700;
const PRIMERA_PLACA_MS = 7_400;
const ENTRE_PLACAS_MS = 500;
const LISTO_MS = 9_800;
/** Cuándo termina la demo (ms): el cierre ya escrito. De ahí en adelante, quieta. */
export const FIN_DE_LA_DEMO_MS = TIPEO.listo[1];

/** Cuatro placas (ADR-2026-10-12-la-demo-arma-cuatro-imagenes; antes tres, y antes cinco). */
export const TOTAL_DE_PLACAS = 4;

export interface EstadoDeLaDemo {
  saludo: boolean;
  /** Cuánto de cada texto ya se escribió, de 0 a 1. */
  escritoSaludo: number;
  pedido: boolean;
  escritoPedido: number;
  pensando: boolean;
  respuesta: boolean;
  escritoRespuesta: number;
  /** Cuánto del carrusel está armado, de 0 a 1 (la barrita). */
  armado: number;
  /** Cuántas placas ya aparecieron (0 a `TOTAL_DE_PLACAS`). */
  placas: number;
  listo: boolean;
  escritoListo: number;
  /** Ya terminó: todo escrito y el carrusel armado. */
  terminada: boolean;
}

const avance = (t: number, [desde, hasta]: readonly [number, number]) =>
  t <= desde ? 0 : t >= hasta ? 1 : (t - desde) / (hasta - desde);

/** Qué se ve a los `ms` de empezada. Pasado el final, queda como al final. */
export function estadoDeLaDemo(ms: number): EstadoDeLaDemo {
  const t = Math.max(0, Math.min(ms, FIN_DE_LA_DEMO_MS));
  const placas = t < PRIMERA_PLACA_MS ? 0 : Math.min(TOTAL_DE_PLACAS, 1 + Math.floor((t - PRIMERA_PLACA_MS) / ENTRE_PLACAS_MS));
  return {
    saludo: t >= SALUDO_MS,
    escritoSaludo: avance(t, TIPEO.saludo),
    pedido: t >= PEDIDO_MS,
    escritoPedido: avance(t, TIPEO.pedido),
    pensando: t >= PIENSA_DESDE_MS && t < RESPUESTA_MS,
    respuesta: t >= RESPUESTA_MS,
    escritoRespuesta: avance(t, TIPEO.respuesta),
    armado: avance(t, [TIPEO.respuesta[0], ARMADO_HASTA_MS]),
    placas,
    listo: t >= LISTO_MS,
    escritoListo: avance(t, TIPEO.listo),
    terminada: t >= FIN_DE_LA_DEMO_MS,
  };
}

/** Cuántas letras de un texto de `largo` se ven con ese avance. */
export function letrasEscritas(largo: number, escrito: number): number {
  return Math.max(0, Math.min(largo, Math.floor(largo * escrito)));
}

/** Cómo queda la demo al terminar (y de entrada, para quien pide menos movimiento). */
export const DEMO_TERMINADA: EstadoDeLaDemo = estadoDeLaDemo(FIN_DE_LA_DEMO_MS);
